import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { DateFieldCalendar, IDateFieldCalendarProps } from '@gem/components';
import { ICommonPopupsProps } from '@gem/popups';
import dateService from '@gem/services/date-service';

import Tasks from 'APP/Tasks';
import { timeValidation } from 'APP/packages/validations/fields/fields';

export interface IScheduleSettingsPopupParams {
  date?: Date;
  onSubmit(data: Date): Promise<void>;
}

interface IScheduleSettingsPopupPresenter {
  dateTime: Date;
  minDate: Date;
  initialValues: IScheduleSettingsFormValues;
  init(): void;
  onValidateForm(values: IScheduleSettingsFormValues): Record<string, string>;
  onSubmit(values: IScheduleSettingsFormValues): void;
  onFocusDateField(props: IDateFieldCalendarProps): void;
  onClose(): void;
}

export interface IScheduleSettingsFormValues {
  date: Date;
  time: string;
}

export const useScheduleSettingsPopupPresenter = (
  data: ICommonPopupsProps<IScheduleSettingsPopupParams>
): IScheduleSettingsPopupPresenter => {
  const { popupInstance, onClose, params } = data;

  const presenter = useLocalStore<IScheduleSettingsPopupPresenter>(() => ({
    dateTime: dateService.nowWithOffset(0, 0, 10),

    init(): void {
      if (params?.date) {
        presenter.dateTime = new Date(params.date);
      }
    },

    get minDate(): Date {
      return dateService.now();
    },

    get initialValues(): IScheduleSettingsFormValues {
      return {
        date: presenter.dateTime,
        time: dateService.time(presenter.dateTime, true) || '',
      };
    },

    onValidateForm(values: IScheduleSettingsFormValues): Record<string, string> {
      let minTime = null;
      if (dateService.isToday(values.date)) {
        minTime = dateService.time(dateService.now(), true);
      }

      const timeValidationResult = timeValidation({
        value: values.time,
        data: { minTime, errorText: ' ' },
      });

      if (timeValidationResult) {
        return { time: timeValidationResult };
      }

      return {};
    },

    async onSubmit(values: IScheduleSettingsFormValues): Promise<void> {
      const newDateTime = new Date(values.date);
      const timeValues = dateService.parseTime(values.time);

      if (timeValues) {
        newDateTime.setHours(Number(timeValues[0]), Number(timeValues[1]), 0);
      }

      await params!.onSubmit(newDateTime);
      presenter.onClose();
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onFocusDateField(props: IDateFieldCalendarProps): void {
      const { value, onChoose, minDate } = props;

      Tasks.app.openPopup(DateFieldCalendar, {
        params: { value, onChoose, minDate },
      });
    },
  }));

  useEffect(() => {
    presenter.init();
  }, []);

  return presenter;
};
